/**
 * jobcrawls wiring for inspector mode. Computes `available` from the app's auth
 * (reviewer allowlist / translation_reviewer role), a kill switch, and the
 * backoffice testing-page opt-in cookie, then mounts the generic
 * InspectorProvider + the in-place overlay once at the app root (LayoutDefault).
 * Non-reviewers (or reviewers who haven't opted in on the testing page) get
 * `available={false}`, so the overlay renders nothing and the toggle never
 * appears — even if the flag is forced.
 *
 * Deliberately does not import backoffice-auth.ts's `backofficeAuthAdapter`: that
 * module pulls in the admin email allowlist, which must never reach client-bundled
 * code. `hasBackofficeAccess` is a plain boolean computed server-side instead.
 *
 * Kill switch: set VITE_I18N_INSPECTOR_DISABLED=1 to disable instantly.
 *
 * Off-by-default: the overlay only ever appears once a reviewer has flipped
 * "Translations Inspector" on from the backoffice testing page — the cookie
 * defaults to unset/false, so a plain SSR-then-hydrate render never flashes it.
 */
import React, { useEffect, useState } from 'react';
import { useAuthOptional } from '../../contexts/AuthContext';
import { TRANSLATION_REVIEWER_ROLE } from './translation-reviewer-role';
import { InspectorProvider } from '../client/inspector';
import { InspectorOverlay } from '../client/InspectorOverlay';
import { readFeatureFlagFromDocumentCookie } from '../../utils/feature-flags/read-feature-flag';
import { I18N_INSPECTOR_COOKIE } from '../../utils/feature-flags/i18n-inspector-flag';

const KILLED =
  (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_I18N_INSPECTOR_DISABLED ===
  '1';

function readTestingPageOptIn(): boolean {
  return readFeatureFlagFromDocumentCookie(I18N_INSPECTOR_COOKIE, false);
}

export function InspectorMount({ children }: { children: React.ReactNode }): React.ReactElement {
  const auth = useAuthOptional();
  const canReview =
    Boolean(auth?.user?.roles?.includes(TRANSLATION_REVIEWER_ROLE)) || Boolean(auth?.hasBackofficeAccess);

  // Starts false to match SSR, then picks up the testing-page cookie post-hydrate.
  const [optedInFromTestingPage, setOptedInFromTestingPage] = useState(false);
  useEffect(() => {
    setOptedInFromTestingPage(readTestingPageOptIn());
  }, []);

  const available = !KILLED && canReview && optedInFromTestingPage;
  return (
    <InspectorProvider available={available}>
      {children}
      <InspectorOverlay />
    </InspectorProvider>
  );
}
